import { useMemo, useState } from "react";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { z } from "zod";
import { AdminShell } from "@/components/layout/admin-shell";
import { splitName } from "@/lib/split-name";
import { appSidebarSections, appBottomNavItems } from "@/lib/app-sidebar";
import { adminWeekOptions } from "@/api/hours-entries/query-options";
import { UrenPageHeader } from "@/components/admin/uren/uren-page-header";
import { WeekTable, groupKey as computeGroupKey } from "@/components/admin/uren/week-table";
import { SelectionDrawer } from "@/components/admin/uren/selection-drawer";
import { UrenInsertSheet } from "@/components/admin/uren/uren-insert-sheet";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { isoWeekFromDate, nextWeek, prevWeek, weekDatesFromIsoYearWeek } from "@/lib/iso-week";
import type { RoleKind } from "@/components/vroom/role-pill";

const ZERO_COUNTS = { todo: 0, ok: 0, rejected: 0, exported: 0 };
const EMPTY_RELATED: {
  users: { id: string; name: string }[];
  workorders: { id: string; number: string }[];
  cranes: { id: string; number: string }[];
} = { users: [], workorders: [], cranes: [] };

export const urenSearchSchema = z.object({
  year: z.coerce.number().int().optional(),
  week: z.coerce.number().int().optional(),
  group: z.enum(["worker", "workorder-crane"]).optional(),
  tab: z.enum(["todo", "ok", "rejected", "exported"]).optional(),
  cc: z.string().optional(),
  wo: z.string().optional(),
  kr: z.string().optional(),
  w: z.string().optional(),
});

function buildAdminWeekParams(search: z.infer<typeof urenSearchSchema>) {
  const fallback = isoWeekFromDate(new Date());
  const isoYear = search.year ?? fallback.isoYear;
  const isoWeek = search.week ?? fallback.isoWeek;
  const groupBy = search.group ?? "worker";
  const tab = search.tab ?? "todo";
  return {
    isoYear,
    isoWeek,
    groupBy,
    ...(tab ? { tab } : {}),
    ...(search.cc ? { costCenterId: search.cc } : {}),
    ...(search.wo ? { workorderId: search.wo } : {}),
    ...(search.kr ? { craneId: search.kr } : {}),
    ...(search.w ? { workerId: search.w } : {}),
  };
}

export const Route = createFileRoute("/_authed/admin/uren")({
  beforeLoad: ({ context }) => {
    const user = (context as { user?: { role?: string } }).user;
    if (user?.role !== "super_admin" && user?.role !== "approver") {
      throw redirect({ to: "/work", search: {} });
    }
  },
  validateSearch: urenSearchSchema,
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    const qc = (context as { queryClient: QueryClient }).queryClient;
    void qc.prefetchQuery(adminWeekOptions(buildAdminWeekParams(deps)));
  },
  component: UrenList,
});

interface SessionUser {
  name: string;
  email: string;
  role?: RoleKind;
}

export function UrenList() {
  const { t } = useTranslation();
  const { user: sessionUser } = Route.useRouteContext() as { user: SessionUser };
  const { firstName, lastName } = splitName(sessionUser.name);
  const role: RoleKind = sessionUser.role ?? "worker";
  const search = Route.useSearch();
  const navigate = useNavigate();

  const today = useMemo(() => new Date(), []);
  const defaults = useMemo(() => isoWeekFromDate(today), [today]);
  const isoYear = search.year ?? defaults.isoYear;
  const isoWeek = search.week ?? defaults.isoWeek;
  const groupBy = search.group ?? "worker";
  const tab = search.tab ?? "todo";

  const queryParams = buildAdminWeekParams(search);
  const { data, isPending } = useQuery({
    ...adminWeekOptions(queryParams),
    placeholderData: keepPreviousData,
  });
  const dates = useMemo(() => weekDatesFromIsoYearWeek(isoYear, isoWeek), [isoYear, isoWeek]);

  const qc = useQueryClient();
  function prefetch(next: Partial<z.infer<typeof urenSearchSchema>>) {
    void qc.prefetchQuery(adminWeekOptions(buildAdminWeekParams({ ...search, ...next })));
  }

  const [insertOpen, setInsertOpen] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => new Set());
  const selectedGroups = data?.groups.filter((g) => selectedKeys.has(computeGroupKey(g))) ?? [];
  function toggleSelect(key: string) {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }
  function clearSelection() {
    setSelectedKeys(new Set());
  }

  function go(next: Partial<z.infer<typeof urenSearchSchema>>) {
    navigate({
      to: "/admin/uren",
      search: { ...search, ...next } as never,
      viewTransition: false,
    });
  }

  return (
    <AdminShell
      user={{ firstName, lastName, role }}
      section="admin"
      breadcrumb={<>{t("admin.uren.breadcrumb")}</>}
      sidebarSections={appSidebarSections({ active: "uren", role }, t)}
      bottomNavItems={appBottomNavItems({ section: "admin", active: "uren", role }, t)}
    >
      <div className="flex justify-end gap-2 px-4 pt-3 md:px-6">
        <Button type="button" variant="primary" size="default" onClick={() => setInsertOpen(true)}>
          <Plus size={14} />
          {t("admin.uren.insert.trigger")}
        </Button>
      </div>
      <UrenPageHeader
        isoYear={isoYear}
        isoWeek={isoWeek}
        dates={dates}
        view={groupBy}
        onViewChange={(v) => go({ group: v })}
        onPrevWeek={() => {
          const p = prevWeek(isoYear, isoWeek);
          go({ year: p.isoYear, week: p.isoWeek });
        }}
        onNextWeek={() => {
          const n = nextWeek(isoYear, isoWeek);
          go({ year: n.isoYear, week: n.isoWeek });
        }}
        onTodayWeek={() => go({ year: defaults.isoYear, week: defaults.isoWeek })}
        onPrefetchTab={(t) => prefetch({ tab: t })}
        onPrefetchView={(v) => prefetch({ group: v })}
        onPrefetchPrevWeek={() => {
          const p = prevWeek(isoYear, isoWeek);
          prefetch({ year: p.isoYear, week: p.isoWeek });
        }}
        onPrefetchNextWeek={() => {
          const n = nextWeek(isoYear, isoWeek);
          prefetch({ year: n.isoYear, week: n.isoWeek });
        }}
        tab={tab}
        counts={data?.meta.counts ?? ZERO_COUNTS}
        onTabChange={(next) => go({ tab: next })}
        filterValue={{
          costCenterId: search.cc,
          workorderId: search.wo,
          craneId: search.kr,
          workerId: search.w,
        }}
        onFilterChange={(v) =>
          go({
            cc: v.costCenterId,
            wo: v.workorderId,
            kr: v.craneId,
            w: v.workerId,
          })
        }
        related={data?.meta.related ?? EMPTY_RELATED}
      />
      <WeekTable
        data={data}
        dates={dates}
        tab={tab}
        selectedKeys={selectedKeys}
        onToggleSelect={toggleSelect}
        isLoading={isPending}
      />
      <SelectionDrawer
        groups={selectedGroups}
        isoYear={isoYear}
        isoWeek={isoWeek}
        onClear={clearSelection}
      />
      <UrenInsertSheet open={insertOpen} onOpenChange={setInsertOpen} />
    </AdminShell>
  );
}
